/* eslint-disable max-len */
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Route, Redirect, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, styles } from 'yankees-react-utils';
import usePlayerSelector from '../../utils/usePlayerSelector';
import { useOktaQuery, Game } from '../../data';
import RouterTabs from '../../components/RouterTabs';
import GameSelector from '../../components/GameSelector';
import PPFX from './PPFX/PPFX';
import PitchExecution from './PitchExecution/PitchExecution';
import Receiving from './Receiving/Receiving';
import Blocking from './Blocking/Blocking';
import Throwing from './Throwing/Throwing';
import Hitter from './Hitter/Hitter';
import Fielding from './Fielding/Fielding';
import InGameSpeed from './InGameSpeed';
import MlbamGameSummary from './Live/MlbamGameSummary';
import { store } from '../../store';

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 20px 20px 0;
`;
const PlayerSelection = styled.div`
    display: flex;
    h2 {
        font-weight: bold;
        margin-right: 25px;
    }
    h3 {
        margin-left:10px;
        margin-top:-6px;
    }
`;

const PostGameHome = ({ match: { path, url, params } }) => {
    const gameid = params.gameId && Number(params.gameId);
    const { t: translate } = useTranslation();
    const { PlayerID: playerId, IsPitcher } = usePlayerSelector();
    let { TeamID } = usePlayerSelector();
    const [selectedGameId, setSelectedGameId] = useState(gameid);
    const { state: { user: loggedInUser } } = useContext(store);
    const roles = loggedInUser.Roles?.map((r) => r.RoleName);
    const history = useHistory();
    const userCanViewCTWB = roles?.some((role) => ['affiliateanalyst', 'mjcoach', 'mncoach', 'nyfrontoffice', 'playerdev', 'videomanager', 'viewmjadvance', 'viewmnadvance', 'player'].includes(role));
    // fetch generic data about this game
    const { data: homepage, isLoading } = useOktaQuery(['game', gameid], (t) => Game.homepage(t, { gameid }), { enabled: !!gameid });
    const gameinfo = homepage?.gameinfo[0];
    // fetch our data about this player and game
    // this will tell us if we have built stats for player in this game
    const { data: playerGameData, isLoading: loadingPlayerGameData } = useOktaQuery(
        ['gamesbyplayerstats', { playerId, gameId: gameid }],
        (t) => Game.byplayerstats(t, { playerId, gameId: gameid }),
    );
    const game = playerGameData && playerGameData.games[0];
    if (gameinfo == null || loadingPlayerGameData) return (<LoadingSpinner />);
    // if player is a catcher and they're no longer on the team they were on for this game, we need to get whichever team was the NYY org team and use that teamid
    if (game?.iscatcher && homepage?.pstats.filter((p) => p.teamid === TeamID).length === 0) {
        TeamID = gameinfo.awayteamorg === 31 ? gameinfo.awayteamid : gameinfo.hometeamid;
    }
    const tabs = [];

    if (gameinfo) gameinfo.gameid = gameid;

    // no stats for this player as hitter, pitcher, or fielder? show them the mlbam data tab
    if (gameinfo?.mlbamgamepk && game && ((!game?.ispitcher && !game?.ishitter && !game?.isfielder && !game?.iscatcher) || game?.hasbpvideo)) {
        tabs.push({
            key: 'live',
            path: `${url}/live`,
            title: IsPitcher ? translate('sections.post-game.home.pitchfx-breakdown') : translate('sections.post-game.home.at-bats'),
            component: () => {
                if (!gameinfo || !playerId) return null;
                if (IsPitcher) return <PPFX playerid={playerId} gameid={gameid} game={gameinfo} isGameLoading={isLoading} isInBats={game?.inbats} isLive />;
                return (<MlbamGameSummary playerid={playerId} gameid={gameid} gameinfo={gameinfo} />);
            },
        });
    }

    if (game?.ispitcher) {
        tabs.push({
            key: 'ppfx',
            path: `${url}/ppfx`,
            title: translate('sections.post-game.home.pitchfx-breakdown'),
            component: () => gameid && playerId && (
                <PPFX
                    playerid={playerId}
                    gameid={gameid}
                    game={gameinfo}
                    isGameLoading={isLoading}
                    isInBats={game.inbats}
                />
            ),
        });
    }

    if (game?.ishitter) {
        tabs.push({
            key: 'hitter',
            path: `${url}/hitter`,
            title: translate('sections.post-game.home.at-bats'),
            component: () => {
                if (!gameinfo || !playerId) return null;
                return (<Hitter playerid={playerId} gameid={gameid} gameinfo={gameinfo} isInBats={game.inbats} />);
            },
        });
    }

    if (game?.isfielder && !game?.iscatcher) {
        tabs.push({
            key: 'fielding',
            path: `${url}/fielding`,
            title: 'Fielding',
            component: () => {
                if (!gameinfo || !playerId) return null;
                return (<Fielding playerid={playerId} gameid={gameid} />);
            },
        });
    }

    if (game?.isfielder || game?.ishitter) {
        tabs.push({
            key: 'in-game-speed',
            path: `${url}/in-game-speed`,
            title: translate('sections.post-game.home.in-game-speed'),
            component: () => <InGameSpeed playerId={playerId} gameInfo={gameinfo} />,
        });
    }

    if (game?.iscatcher) {
        tabs.push({
            key: 'receiving',
            path: `${url}/receiving`,
            title: translate('sections.post-game.home.receiving'),
            component: () => {
                if (!gameinfo || !playerId) return null;
                return (<Receiving playerid={playerId} gameid={gameid} gameinfo={gameinfo} />);
            },
        }, {
            key: 'blocking',
            path: `${url}/blocking`,
            title: translate('sections.post-game.home.blocking'),
            component: () => {
                if (!gameinfo || !playerId) return null;
                return (<Blocking playerid={playerId} gameid={gameid} gameinfo={gameinfo} />);
            },
        }, {
            key: 'throwing',
            path: `${url}/throwing`,
            title: translate('sections.post-game.home.throwing'),
            component: () => {
                if (!gameinfo || !playerId) return null;
                return (<Throwing playerid={playerId} gameid={gameid} gameinfo={gameinfo} />);
            },
        });
    }

    if ((game?.ispitcher || game?.iscatcher) && userCanViewCTWB) {
        const isCatcher = !!game?.iscatcher;
        tabs.push({
            key: 'pitch-execution',
            path: `${url}/pitch-execution`,
            title: translate('sections.post-game.home.pitch-exec-review'),
            component: () => {
                const pstats = isCatcher
                    ? homepage?.pstats.filter((p) => p.teamid === TeamID)
                    : homepage?.pstats.find((p) => p.playerid === playerId);
                if (!gameinfo || !pstats) return null;

                const { teamid } = pstats.length && pstats.length > 0 ? pstats[0] : pstats;
                const vsteamid = teamid === gameinfo.hometeamid ? gameinfo.awayteamid : gameinfo.hometeamid;
                return (<PitchExecution playerid={playerId} gameid={gameid} teamid={teamid} vsteamid={vsteamid} isCatcher={isCatcher} yearid={gameinfo.yearid} isInBats={game.inbats} venueid={gameinfo.ismajorleague === 1 ? gameinfo.venueid : 0} />);
            },
        });
    }

    const navigateToGame = (g) => {
        history.push(`/post-game/${g.value}`);
        setSelectedGameId(g.value);
    };

    // eslint-disable-next-line no-nested-ternary
    const defaultReport = tabs[0]?.key ?? '';
    return (
        <>
            <Route path={path} exact render={() => <Redirect to={{ pathname: `${url}/${defaultReport}` }} />} />
            <div className="text-center">
                <Header>
                    <PlayerSelection>
                        <h2>{translate('sections.post-game.home.post-game-review')}</h2>
                        {playerId && <GameSelector yearId={gameinfo.yearid} gameid={selectedGameId} playerId={playerId} onSelect={navigateToGame} />}
                    </PlayerSelection>
                </Header>
                {tabs.length
                    ? <RouterTabs tabs={tabs} activeColor={styles.theme.background.gallery} />
                    : (
                        <h3 className="d-flex justify-content-center py-5">
                            {translate('sections.post-game.home.no-data-built')}
                        </h3>
                    )}
            </div>
        </>
    );
};

PostGameHome.propTypes = {
    match: PropTypes.shape({
        path: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        params: PropTypes.shape({
            gameId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }).isRequired,
    }).isRequired,
};

export default PostGameHome;
